import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { Box, Button, Card, CardActionArea, Chip, Grid, Link as MuiLink, Stack, Typography } from "@mui/material";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAppSelector } from "../../Hooks";
import { getUserInfo } from "../../store/user-process/selector";
import { getPersonalInfo } from "../../store/integration-process/selector";
import { AppRoute, LocalStorageMethod } from "../../enums/enum";
import LicDialog from "../../Components/dialog/lic/LicDialog";
import AddLic from "../../Components/dialog/lic/AddLic";
import QueryReveipt from "../../Components/dialog/lic/QueryReceipt";
import PageLayout from "../../Components/page-layout/PageLayout";
import LocalStorageProvider from "../../utils/locaStorageProvider";
import { brand } from "../../theme";

type Tile = {
  title: string;
  text: string;
  icon: React.ReactNode;
  onClick: () => void;
  badge?: number;
  needsLic?: boolean;
};

function TileCard({ tile }: { tile: Tile }) {
  return (
    <Card sx={{ height: "100%", transition: "border-color .15s, box-shadow .15s", "&:hover": { borderColor: brand.main, boxShadow: "0 6px 20px rgba(16,24,40,.08)" } }}>
      <CardActionArea onClick={tile.onClick} sx={{ height: "100%", p: 2.5, display: "flex", alignItems: "flex-start", justifyContent: "flex-start", gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(brand.main, 0.1),
            color: brand.main,
          }}
        >
          {tile.icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">{tile.title}</Typography>
            {tile.badge !== undefined && tile.badge > 0 && <Chip label={tile.badge} size="small" color="primary" />}
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 14.5 }}>
            {tile.text}
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ color: brand.muted, alignSelf: "center" }} />
      </CardActionArea>
    </Card>
  );
}

export default function Main() {
  const [openDialogAddLic, setOpenDialogAddLic] = useState(false);
  const [openKvitDialog, setOpenKvitDialog] = useState(false);
  const navigate = useNavigate();
  const userInfo = useAppSelector(getUserInfo);
  const personalInfo = useAppSelector(getPersonalInfo);
  if (userInfo.lics?.length === 0) {
    window.location.href = AppRoute.Lic;
  }
  const hasLic = userInfo.lics?.length !== 0;
  const defaultLic = LocalStorageProvider(userInfo.userId, null, LocalStorageMethod.getItem);
  // «Иванов Иван Иванович» → «Иван Иванович»; без ФИО — просто «Здравствуйте!»
  const fioParts = userInfo.fio?.trim() ? userInfo.fio.trim().split(/\s+/) : [];
  const name = fioParts.length > 1 ? fioParts.slice(1, 3).join(" ") : fioParts[0] ?? "";

  const tiles: Tile[] = [
    { title: "Передача показаний", text: "Внесите показания счётчиков до 25 числа", icon: <WaterDropOutlinedIcon />, onClick: () => navigate(AppRoute.SendReadings), needsLic: true },
    { title: "Архив квитанций", text: "Запросите квитанции за прошлые месяцы на почту", icon: <ReceiptLongOutlinedIcon />, onClick: () => setOpenKvitDialog(true), needsLic: true },
    { title: "История оплат", text: "Все платежи по лицевому счёту", icon: <PaymentsOutlinedIcon />, onClick: () => navigate(AppRoute.HistoryPayment), needsLic: true },
    { title: "Приборы учёта", text: "Счётчики, даты поверки и последние показания", icon: <SpeedOutlinedIcon />, onClick: () => navigate(AppRoute.Counter), needsLic: true },
    { title: "Мои лицевые счета", text: "Добавьте счёт или выберите другой", icon: <BadgeOutlinedIcon />, onClick: () => navigate(AppRoute.Lic), badge: userInfo.lics?.length },
    { title: "Мои обращения", text: "Задайте вопрос и следите за ответом", icon: <ForumOutlinedIcon />, onClick: () => navigate(AppRoute.UserRequests) },
    { title: "Документы", text: "Бланки заявлений и согласия", icon: <FolderOutlinedIcon />, onClick: () => navigate(AppRoute.Documents) },
  ];
  const visibleTiles = tiles.filter((t) => !t.needsLic || hasLic);

  if (!userInfo.userId) return <PageLayout>{null}</PageLayout>;

  return (
    <PageLayout title={name ? `Здравствуйте, ${name}!` : "Здравствуйте!"} subtitle="Личный кабинет клиента — услуги отопления и горячего водоснабжения">
      {hasLic && defaultLic?.lic && (
        <Card
          sx={{
            mb: 3,
            border: "none",
            color: "#fff",
            background: `linear-gradient(135deg, ${brand.slate} 0%, #3e4f5d 60%, #4f5f6c 100%)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", right: -60, bottom: -80, width: 260, height: 260, borderRadius: "50%", bgcolor: alpha(brand.main, 0.22), filter: "blur(48px)" }} />
          <Stack direction={{ xs: "column", md: "row" }} gap={3} alignItems={{ xs: "flex-start", md: "center" }} sx={{ p: { xs: 2.5, sm: 3.5 }, position: "relative" }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ opacity: 0.75, fontSize: 14 }}>Лицевой счёт</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>{defaultLic.lic}</Typography>
              {personalInfo?.address?.fullAddress && <Typography sx={{ opacity: 0.85, mt: 0.5 }}>{personalInfo.address.fullAddress}</Typography>}
            </Box>
            {personalInfo?.currentDebt !== undefined && personalInfo?.currentDebt !== null && (
              <Box sx={{ flexShrink: 0 }}>
                <Typography sx={{ opacity: 0.75, fontSize: 14 }}>Задолженность на сегодня</Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
                  {Number(personalInfo.currentDebt).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                </Typography>
              </Box>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
              <Button variant="contained" size="large" onClick={() => navigate(AppRoute.SendReadings)} startIcon={<WaterDropOutlinedIcon />}>
                Передать показания
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setOpenKvitDialog(true)}
                sx={{ color: "#fff", borderColor: alpha("#fff", 0.5), "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.08) } }}
              >
                Квитанции
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      <Grid container spacing={2}>
        {visibleTiles.map((tile) => (
          <Grid item xs={12} sm={6} lg={4} key={tile.title}>
            <TileCard tile={tile} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <MuiLink href="https://penza.esplus.ru" target="_blank" rel="noopener" color="text.secondary" underline="hover">
          Сайт Пензенского филиала «ЭнергосбыТ Плюс»
        </MuiLink>
      </Box>

      <AddLic Open={openDialogAddLic} Close={setOpenDialogAddLic} Title="Добавить лицевой счет" />
      <LicDialog Open={openKvitDialog} Close={setOpenKvitDialog} Title="Архив квитанций" Component={<QueryReveipt />} />
    </PageLayout>
  );
}
